/**
 * useEmployeeDashboard.js
 * -----------------------
 * Custom hook coordinating data fetching, live clock ticker, and state
 * for the Employee Self-Service Dashboard.
 */
import { useState, useEffect, useCallback } from 'react';
import { dashboardApi } from '../api/dashboardApi';
import { appToast } from '../../../../components/AppToast';

export function useEmployeeDashboard() {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Live clock ticker (updates every second for real-time punch clock)
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const loadDashboard = useCallback(async (isSilent = false) => {
    if (!isSilent) setIsLoading(true);
    else setIsRefreshing(true);

    try {
      const res = await dashboardApi.getSummary();
      setData(res);
    } catch (err) {
      console.warn('Failed to load employee dashboard summary:', err);
      appToast.error('Unable to fetch dashboard updates. Showing cached view.');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const handleRefresh = () => {
    loadDashboard(true);
  };

  return {
    data,
    isLoading,
    isRefreshing,
    currentTime,
    handleRefresh,
  };
}
